import { IDisclosure, IDisclosurePermissions } from '@vivli/features/disclosures/infrastructure/interface';
import { IUser } from '@vivli/shared/infrastructure/interface';
import { DisclosureStatusEnum } from '@vivli/features/disclosures/infrastructure/enum';
import { IDataRequest } from '@vivli/features/data-requests/infrastructure/interface';
import { useUserPermissions } from '@vivli/features/users/infrastructure/hook';
import { PermissionsEnum } from '@vivli/shared/infrastructure/enum';

export const useDisclosurePermissions = (disclosure: IDisclosure, dataRequest: IDataRequest, user: IUser): IDisclosurePermissions => {
    const { studyDisclosureDetails, status, contributingOrgDetails } = disclosure;
    const { requestedStudies, researchTeam } = dataRequest;
    const { isOrgAdmin } = useUserPermissions();

    const isOrgAdminForStudy = requestedStudies?.some((rs) => isOrgAdmin(rs.organizationId));

    const isVivliAdmin = user.vivliPermissions.includes(PermissionsEnum.DrActAdv);

    const studiesForUserOrg = studyDisclosureDetails?.filter((is) => user.orgMemberships.some((om) => om.orgId === is.organizationId));

    const orgHasIncludedStudies = isOrgAdminForStudy && studiesForUserOrg?.length > 0;

    const orgIsReviewComplete = contributingOrgDetails?.some((cod) =>
        studiesForUserOrg.some((sfg) => sfg.organizationId === cod.organizationId && cod.finalized)
    );

    const isOnResearchTeam = researchTeam.researchTeamDetails?.some((tm) => tm.userId === user.id);

    const allowOwnerDelete = (isOrgAdminForStudy && !orgIsReviewComplete) || isOnResearchTeam;

    const defaultPermissions: IDisclosurePermissions = {
        save: isVivliAdmin,
        resetToDraft: isVivliAdmin,
        published: isVivliAdmin,
        notPublished: isVivliAdmin,
        editDisclosureInfo: isVivliAdmin,
        editStudies: isVivliAdmin,
        internalNotes: isVivliAdmin,
        actAsOrg: isVivliAdmin,
        deleteAttachments: isVivliAdmin,
        viewAllAttachments: isVivliAdmin || isOnResearchTeam,
        viewAttachments: isVivliAdmin || isOrgAdminForStudy,
        markAllReviewsComplete: isVivliAdmin,
        copyAcknowledgement: isVivliAdmin,
        downloadReviewDocs: true,
        uploadReviewDocs: isVivliAdmin,
        editReviewDocs: isVivliAdmin || isOnResearchTeam, //all modifications to review docs + actions on associated controls
    };

    switch (status) {
        case DisclosureStatusEnum.Draft:
            return {
                save: isVivliAdmin || isOnResearchTeam,
                submit: isVivliAdmin || isOnResearchTeam,
                editDisclosureInfo: isVivliAdmin || isOnResearchTeam,
                editStudies: isVivliAdmin || isOnResearchTeam,
                editAcknowledgement: isVivliAdmin || isOnResearchTeam,
                copyAcknowledgement: isVivliAdmin || isOnResearchTeam,
                uploadReviewDocs: isVivliAdmin || isOnResearchTeam,
                internalNotes: isVivliAdmin,
                hideCitations: true,
                actAsOrg: isVivliAdmin,
                deleteAttachments: isVivliAdmin,
                uploadAttachments: isOrgAdminForStudy,
                viewAllAttachments: isVivliAdmin || isOnResearchTeam,
                viewAttachments: isVivliAdmin || isOrgAdminForStudy,
                downloadReviewDocs: isVivliAdmin || isOnResearchTeam,
                editReviewDocs: isVivliAdmin || isOnResearchTeam,
            };
        case DisclosureStatusEnum.ThirtyDayReview:
            return {
                markReviewComplete: isOrgAdminForStudy && orgHasIncludedStudies && !orgIsReviewComplete,
                save: isVivliAdmin || isOrgAdminForStudy,
                resetToDraft: isVivliAdmin,
                editDisclosureInfo: isVivliAdmin,
                editStudies: isVivliAdmin,
                uploadReviewDocs: isVivliAdmin,
                internalNotes: isVivliAdmin,
                actAsOrg: isVivliAdmin,
                uploadAttachments: isOrgAdminForStudy,
                hideCitations: true,
                deleteAttachments: isVivliAdmin,
                downloadReviewDocs: true,
                viewAllAttachments: isVivliAdmin || isOnResearchTeam,
                viewAttachments: isVivliAdmin || isOrgAdminForStudy,
                editReviewDocs: false,
                allowOwnerDelete,
            };
        case DisclosureStatusEnum.ReviewComplete:
            return {
                ...defaultPermissions,
                editReviewDocs: false,
            };
        case DisclosureStatusEnum.Published:
            return {
                ...defaultPermissions,
                notPublished: isVivliAdmin,
                published: false,
                editReviewDocs: false,
            };
        case DisclosureStatusEnum.NotPublished:
            return {
                ...defaultPermissions,
                notPublished: false,
                published: isVivliAdmin,
                editReviewDocs: false,
            };
        case DisclosureStatusEnum.SubmittedToPublisher:
        case DisclosureStatusEnum.Withdrawn:
        default:
            return {};
    }
};
